import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "@/context/AuthContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogClose,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { 
    Loader2, Clock, Activity, Hash, Globe, FileJson, FileText, 
    X as CloseIcon, Copy as CopyIcon, Check as CheckIcon, ChevronDown,
    Image as ImageIcon, AlertCircle, CheckCircle2, ExternalLink, Maximize2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

function formatSize(bytes) {
    if (!bytes || bytes === 0) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
}

function formatResponseData(data, contentType) {
    if (!data) return '';
    
    // Handle string data
    if (typeof data === 'string') {
        // If content type is text or HTML, trim whitespace
        if (contentType?.includes('text') || contentType?.includes('html')) {
            return data.trim().replace(/\s+/g, ' ');
        }
        return data;
    }
    
    // Handle JSON data
    return JSON.stringify(data, null, 2);
}

// TODO: Move this to a shared component
const MinimalBadge = ({ children, className }) => (
    <div className={cn(
        "rounded-sm border border-violet-200/50 bg-violet-50/50 px-2 py-0.5 text-[10px] font-medium uppercase text-violet-700 dark:border-violet-800/50 dark:bg-violet-950/50 dark:text-violet-300",
        className
    )}>
        {children}
    </div>
);

function BrowserAgentHistory({ history }) {
    if (!history || !Array.isArray(history)) return null;

    const [imageLoading, setImageLoading] = useState({});
    const [selectedScreenshot, setSelectedScreenshot] = useState(null);

    const handleImageLoad = (stepIndex) => {
        setImageLoading(prev => ({
            ...prev,
            [stepIndex]: false
        }));
    };

    const openScreenshotModal = (screenshot) => {
        setSelectedScreenshot(screenshot);
    };

    const closeScreenshotModal = () => {
        setSelectedScreenshot(null);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold">Browser Agent Result</h4>
                <Badge variant="secondary" className="bg-muted">
                    {history.length} steps
                </Badge>
            </div>
            
            <div className="relative space-y-6">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
                
                {history.map((step, index) => (
                    <div key={index} className="relative pl-8">
                        {/* Timeline dot */}
                        <div className={cn(
                            "absolute left-3 top-2 w-2 h-2 rounded-full",
                            step.error ? "bg-red-500" : "bg-primary"
                        )} />
                        
                        <Card className="overflow-hidden">
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium">Step {step.stepNumber}</span>
                                            {step.action && (
                                                <Badge variant="outline" className="text-xs">
                                                    {step.action}
                                                </Badge>
                                            )}
                                        </div>
                                        {step.url && (
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <Globe className="h-4 w-4" />
                                                <a 
                                                    href={step.url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="hover:text-primary hover:underline"
                                                >
                                                    {step.url}
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                    {step.error ? (
                                        <AlertCircle className="h-5 w-5 text-red-500" />
                                    ) : (
                                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                                    )}
                                </div>

                                {/* Screenshot */}
                                {step.screenshot && (
                                    <div className="mt-4">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                            <ImageIcon className="h-4 w-4" />
                                            <span>Screenshot</span>
                                        </div>
                                        <div 
                                            className="relative group w-full cursor-pointer"
                                            onClick={() => openScreenshotModal(step.screenshot)}
                                        >
                                            {imageLoading[index] !== false && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-muted/30 rounded-lg">
                                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                                </div>
                                            )}
                                            <img 
                                                src={step.screenshot.startsWith('data:') 
                                                    ? step.screenshot 
                                                    : `data:image/png;base64,${step.screenshot}`}
                                                alt={`Screenshot from step ${step.stepNumber}`}
                                                className={cn(
                                                    "w-full rounded-lg border object-contain",
                                                    imageLoading[index] !== false && "opacity-0"
                                                )}
                                                onLoad={() => handleImageLoad(index)}
                                            />
                                            {/* Centered expand icon overlay */}
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                                                <div className="bg-background/80 p-2 rounded-full">
                                                    <Maximize2 className="h-6 w-6 text-foreground" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Extracted Content */}
                                {step.extractedContent && (
                                    <div className="mt-4">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                            <FileText className="h-4 w-4" />
                                            <span>Extracted Content</span>
                                        </div>
                                        <div className="rounded-lg border bg-muted/30 p-3 text-sm">
                                            {step.extractedContent}
                                        </div>
                                    </div>
                                )}

                                {/* Error Message */}
                                {step.error && (
                                    <div className="mt-4">
                                        <div className="flex items-center gap-2 text-sm text-red-500 mb-2">
                                            <AlertCircle className="h-4 w-4" />
                                            <span>Error</span>
                                        </div>
                                        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/10 p-3 text-sm text-red-600 dark:text-red-400">
                                            {step.error}
                                        </div>
                                    </div>
                                )}

                                {/* Model Action */}
                                {step.modelAction && (
                                    <div className="mt-4">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                            <Activity className="h-4 w-4" />
                                            <span>Model Action</span>
                                        </div>
                                        <div className="rounded-lg border bg-muted/30 p-3">
                                            <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
                                                {JSON.stringify(step.modelAction, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                ))}
            </div>

            {/* Screenshot Lightbox Modal */}
            {selectedScreenshot && (
                <Dialog open={!!selectedScreenshot} onOpenChange={() => closeScreenshotModal()}>
                    <DialogContent className="max-w-6xl p-0 overflow-hidden">
                        <div className="relative">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="absolute right-2 top-2 z-10 bg-background/80 hover:bg-background/90"
                                onClick={closeScreenshotModal}
                            >
                                <CloseIcon className="h-4 w-4" />
                            </Button>
                            <img 
                                src={selectedScreenshot.startsWith('data:') 
                                    ? selectedScreenshot 
                                    : `data:image/png;base64,${selectedScreenshot}`}
                                alt="Screenshot full view"
                                className="w-full h-auto max-h-[90vh] object-contain"
                            />
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

export function RunDetailsDialog({ monitorId, runId, open, onOpenChange }) {
    const [run, setRun] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        headers: false,
        response: true
    });
    const navigate = useNavigate();
    const { toast } = useToast();
    const { axiosInstance } = useAuth();

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast({
                title: "Copied to clipboard",
                description: "Content has been copied to your clipboard.",
            });
        } catch (err) {
            toast({
                title: "Failed to copy",
                description: "Please try copying manually.",
                variant: "destructive",
            });
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    useEffect(() => {
        const fetchRunDetails = async () => {
            if (!runId) return;
            
            try {
                setLoading(true);
                const response = await axiosInstance.get(`/monitors/${monitorId}/runs/${runId}`);
                setRun(response.data);
            } catch (error) {
                console.error("Error fetching run details:", error);
                toast({
                    title: "Error fetching run details",
                    description: "Please try again later.",
                    variant: "destructive",
                });
            } finally {
                setLoading(false);
            }
        };

        fetchRunDetails();
    }, [runId, monitorId, axiosInstance]);

    const handleOpenChange = (open) => {
        if (!open) {
            navigate(`/monitors/${monitorId}`);
        }
        onOpenChange(open);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-3xl h-[85vh] flex flex-col overflow-hidden p-0 gap-0">
                <DialogHeader className="px-6 py-4 border-b bg-background">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <DialogTitle className="text-xl font-semibold">Run Details</DialogTitle>
                            {!loading && run && (
                                <span className={cn(
                                    "text-base font-medium",
                                    run.success ? "text-green-500" : "text-red-500"
                                )}>
                                    {run.success ? 'Successful' : 'Failed'}
                                </span>
                            )}
                        </div>
                    </div>
                    {!loading && run && (
                        <div className="flex items-center gap-2 mt-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">
                                Started at {run.startTime}
                            </p>
                        </div>
                    )}
                </DialogHeader>
                
                {loading ? (
                    <div className="flex-1 flex justify-center items-center">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                ) : run ? (
                    <div className="flex-1 overflow-y-auto">
                        <div className="px-6 py-4 border-b bg-card">
                            <div className="grid grid-cols-3 gap-6">
                                <div>
                                    <p className="text-sm text-muted-foreground mb-1">Response Time</p>
                                    <p className="text-lg font-semibold text-violet-500">
                                        {run.result?.responseTime ? `${run.result.responseTime} ms` : 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground mb-1">Duration</p>
                                    <p className="text-lg font-semibold">
                                        {run.duration ? `${run.duration.toFixed(0)} ms` : 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground mb-2">Action Type</p>
                                    <div className="flex items-center">
                                        <MinimalBadge>
                                            {run.actionType.replace('_', ' ')}
                                            {run.method && `: ${run.method}`}
                                        </MinimalBadge>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-6 space-y-6">
                            {run.note && !run.success && (
                                <div className="rounded-lg border border-red-200 dark:border-red-800 p-4 bg-red-50 dark:bg-red-900/10">
                                    <p className="text-sm font-medium text-red-500 dark:text-red-400 mb-1">Error Message</p>
                                    <p className="text-base text-red-600 dark:text-red-400">{run.note}</p>
                                </div>
                            )}

                            {run.note && run.success && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <h4 className="text-lg font-semibold">Bot Insights</h4>
                                            <span className="text-xs text-muted-foreground">
                                                Added at {run.endTime}
                                            </span>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 px-2"
                                            onClick={() => copyToClipboard(run.note)}
                                        >
                                            {copied ? (
                                                <CheckIcon className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <CopyIcon className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                    <div className={cn(
                                        "rounded-lg border border-violet-200/50 bg-violet-50/50 p-5 text-violet-700 dark:border-violet-800/50 dark:bg-violet-950/50 dark:text-violet-300"
                                    )}>
                                        <p className="text-sm break-all">{run.note}</p>
                                    </div>
                                </div>
                            )}

                            {/* Request Details */}
                            {run.details?.url && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <h4 className="text-lg font-semibold">Request URL</h4>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 px-2"
                                            onClick={() => copyToClipboard(run.details.url)}
                                        >
                                            {copied ? (
                                                <CheckIcon className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <CopyIcon className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                    <div className="rounded-lg border bg-muted/30 p-5">
                                        <p className="text-xs font-mono break-all">{run.details.url}</p>
                                    </div>
                                </div>
                            )}

                            {/* Response Section */}
                            {run.result && run.actionType !== "browser_agent" && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <h4 className="text-lg font-semibold">Result Data</h4>
                                            {run.result.archivedAt && (
                                                <Badge variant="secondary" className="bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                                    Archived
                                                </Badge>
                                            )}
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                {run.result.contentType && (
                                                    <div className="flex items-center gap-1.5">
                                                        <FileJson className="h-3.5 w-3.5" />
                                                        {run.result.contentType.split(';')[0]}
                                                    </div>
                                                )}
                                                {run.result.size > 0 && (
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="h-1 w-1 rounded-full bg-muted-foreground/60" />
                                                        {formatSize(run.result.size)}
                                                    </div>
                                                )}
                                                {run.result.archivedAt && (
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="h-1 w-1 rounded-full bg-muted-foreground/60" />
                                                        {new Date(run.result.archivedAt).toLocaleString()}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 shrink-0 ml-2"
                                            onClick={() => toggleSection('response')}
                                        >
                                            <ChevronDown className={cn(
                                                "h-4 w-4 transition-transform",
                                                expandedSections.response && "rotate-180"
                                            )} />
                                        </Button>
                                    </div>
                                    {expandedSections.response && (
                                        <div className="relative group">
                                            {run.result?.archivedAt ? (
                                                <div className="rounded-lg border bg-violet-50 dark:bg-violet-900/10 p-5">
                                                    <div className="flex items-start gap-4">
                                                        <div className="flex-1">
                                                            <p className="text-sm text-violet-600 dark:text-violet-400">
                                                                This result has been archived due to the organization's retention policy ({run.result.retentionPeriod} minutes).
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : run.result.data ? (
                                                <>
                                                    <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-8"
                                                            onClick={() => copyToClipboard(
                                                                typeof run.result.data === 'string'
                                                                    ? run.result.data
                                                                    : JSON.stringify(run.result.data, null, 2)
                                                            )}
                                                        >
                                                            <CopyIcon className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                    <div className="rounded-lg border bg-muted/30">
                                                        <pre className="p-5 text-xs font-mono whitespace-pre overflow-x-auto max-h-[400px] scrollbar-thin scrollbar-thumb-border scrollbar-track-muted">
                                                            <code>
                                                                {typeof run.result.data === 'string'
                                                                    ? run.result.data
                                                                    : JSON.stringify(run.result.data, null, 2)}
                                                            </code>
                                                        </pre>
                                                    </div>
                                                </>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Browser Agent Prompt */}
                            {run.actionType === "browser_agent" && run.result?.prompt && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <h4 className="text-lg font-semibold">Monitoring Instructions</h4>
                                        </div>
                                    </div>
                                    <div className="rounded-lg border bg-muted/30 p-5">
                                        <p className="text-sm">{run.result.prompt}</p>
                                    </div>
                                </div>
                            )}

                            {/* Headers Section */}
                            {run.result?.headers && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <h4 className="text-lg font-semibold">Result Headers</h4>
                                            <div className="flex items-center gap-1.5">
                                                <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                                                <span className="text-xs text-muted-foreground">
                                                    {Object.keys(run.result.headers).length} headers
                                                </span>
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8"
                                            onClick={() => toggleSection('headers')}
                                        >
                                            <ChevronDown className={cn(
                                                "h-4 w-4 transition-transform",
                                                expandedSections.headers && "rotate-180"
                                            )} />
                                        </Button>
                                    </div>
                                    {expandedSections.headers && (
                                        <div className="rounded-lg border bg-muted/30">
                                            <div className="p-5 space-y-2">
                                                {Object.entries(run.result.headers).map(([key, value]) => (
                                                    <div key={key} className="grid grid-cols-[1fr,2fr] gap-4">
                                                        <span className="text-xs font-medium text-muted-foreground">{key}</span>
                                                        <span className="text-xs font-mono break-all">{value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Browser Agent History */}
                            {run.actionType === "browser_agent" && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <h4 className="text-lg font-semibold">Browser Agent History</h4>
                                            {run.result?.archivedAt && (
                                                <Badge variant="secondary" className="bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                                    Archived
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {run.result?.archivedAt ? (
                                        <div className="rounded-lg border bg-violet-50 dark:bg-violet-900/10 p-5">
                                            <div className="flex items-start gap-4">
                                                <div className="flex-1">
                                                    <p className="text-sm text-violet-600 dark:text-violet-400">
                                                        This result has been archived due to the organization's retention policy ({run.result.retentionPeriod} minutes).
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : run.result?.history ? (
                                        <BrowserAgentHistory history={run.result.history} />
                                    ) : null}
                                </div>
                            )}

                            {/* Browser Info */}
                            {run.actionType === "browser_agent" && run.result?.browserInfo && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-lg font-semibold">Browser Information</h4>
                                    </div>
                                    <div className="rounded-lg border bg-muted/30 p-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-sm text-muted-foreground">Browser Type</p>
                                                <p className="text-sm font-medium">
                                                    {run.result.browserInfo.useLocalBrowser ? "Local Browser" : "Steel Browser"}
                                                </p>
                                            </div>
                                            {run.result.browserInfo.steelSessionId && (
                                                <div>
                                                    <p className="text-sm text-muted-foreground">Steel Session ID</p>
                                                    <p className="text-sm font-medium font-mono">
                                                        {run.result.browserInfo.steelSessionId}
                                                    </p>
                                                </div>
                                            )}
                                            {run.result.browserInfo.tracePath && (
                                                <div className="col-span-2">
                                                    <p className="text-sm text-muted-foreground">Trace Path</p>
                                                    <p className="text-sm font-medium font-mono">
                                                        {run.result.browserInfo.tracePath}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex justify-center items-center py-8 text-muted-foreground">
                        Run details not found
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
} 
